import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  Link,
  Typography,
  CardContent,
  CardMedia,
  GridProps,
} from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Image Imports
import travel from "../../assets/images/Travel/travel (1).png";
import roadside from "../../assets/images/Travel/roadside-assistance.png";
import gadget from "../../assets/images/Travel/gadgets.png";
import home from "../../assets/images/Travel/mortgage-loan.png";
import income from "../../assets/images/Travel/income-insurance.png";
import cyber from "../../assets/images/Travel/cyber-security.png";

// Types
interface Product {
  icon: string;
  title: string;
  description?: string;
  navigator?: string;
  pointsOne?: string;
  pointsTwo?: string;
  pointsThree?: string;
  pointsFour?: string;
  navigatorOne?: string;
  navigatorTwo?: string;
  navigatorThree?: string;
  navigatorFour?: string;
}

// Product List Data
const OUR_PRODUCTS_LIST: Product[] = [
  {
    icon: travel,
    title: "Travel Assistance",
    description: "24/7 assistance for unexpected events during your trips.",
    navigator: "/travel-assistance",
  },
  {
    icon: roadside,
    title: "Automotive Assistance",
    description:
      "Roadside assistance, towing, and on-demand support for your journeys.",
    navigator: "/automotive-assistance",
  },
  {
    icon: gadget,
    title: "Mobile Protection",
    description:
      "Smart protection meets seamless services - for the devices you rely on.",
    navigator: "/mobile-protection",
  },
  {
    icon: home,
    title: "Home Appliances",
    description:
      "From stolen gadgets to CCTV support - we secure everything in it.",
    navigator: "/home-appliances",
  },
  {
    icon: income,
    title: "Income Protection",
    description:
      "Help with EMIs, sudden hospital costs, and job loss stress - so you stay focused.",
    navigator: "/income-protect",
  },
  {
    icon: cyber,
    title: "Cyber Security",
    description:
      "We stop threats before they reach you. Browse, bank, and live worry-free.",
    navigator: "/cyber-security",
  },
];

// Animation Variants for Framer Motion
const cardAnimationVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.42, 0, 0.58, 1] as any, // cubic-bezier, avoids TypeScript error (see framer-motion docs)
      delay: i * 0.22,
    },
  }),
};

const styles = {
  OurProductMainContainer: {
    py: 4,
    px: { xs: 1, sm: 1, md: 2, lg: 3 },
    textAlign: "center" as const,
  },
  CardContainer: {
    borderRadius: 3,
    textAlign: "left" as const,
    width: { xs: 315, sm: 400, md: 292, lg: 280, xl: 280 },
    backgroundColor: "white",
    height: 300,
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "flex-start" as const,
    padding: "10px",
    boxShadow: "4px 4px 4px 0 #e7dedaff",
    transition: "border-color 0.3s ease, box-shadow 0.3s ease",
  },
  IconContainer: {
    mt: 0,
    pl: 2,
    width: 80,
    height: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 1,
    overflow: "hidden",
  },
  CardMedia: {
    width: "100%",
    height: "100%",
    objectFit: "contain" as const,
  },
  CardContent: {
    display: "flex",
    flexDirection: "column" as const,
    padding: "10px 16px",
    justifyContent: "space-between",
    flexGrow: 1,
  },
  ProductLabelTxt: {
    color: "black",
    fontWeight: 600,
    fontFamily: '"Poppins", serif',
    textAlign: "left" as const,
    fontSize: { xs: "0.92rem", sm: "0.92rem", md: "1rem" },
  },
  ProductDescriptionTxt: {
    mb: 2,
    color: "text.secondary",
    fontFamily: '"Poppins", serif',
    fontSize: { xs: "0.8rem", sm: "0.8rem", md: "0.9rem" },
    textAlign: "justify" as const,
  },
  LearnMoreButtonTxt: {
    fontWeight: 500,
    color: "black",
    fontFamily: '"Poppins", serif',
    fontSize: { xs: "0.92rem", sm: "0.92rem", md: "0.92rem" },
    border: "1px solid black",
    padding: "10px",
    textAlign: "left" as const,
    width: { md: "7.5rem", xs: "auto" },
    "&:hover": {
      backgroundColor: "black",
      color: "white",
    },
  },
  OthersLabelTxt: {
    mb: { xs: 0.8, sm: 0.8, md: 0, lg: 0.8 },
    cursor: "pointer",
    textDecoration: "none",
    color: "text.secondary",
    fontFamily: '"Poppins", serif',
    fontSize: { xs: "0.8rem", sm: "0.8rem", md: "0.9rem" },
    textAlign: "left" as const,
    "&:hover": { fontWeight: 600 },
  },
};

// Card List Component
const ProductCardList: React.FC<{
  products: Product[];
  revealedCount: number;
  onNavigate: ReturnType<typeof useNavigate>;
}> = ({ products, revealedCount, onNavigate }) => (
  <Grid container spacing={2} py={2} justifyContent="center">
    {products.map((item, index) => (
      <Grid
        component="div"
        item
        xs={12}
        sm={6}
        md={3}
        key={index}
        {...({ item: true } as GridProps)}
      >
        <motion.div
          custom={index}
          initial="hidden"
          animate={revealedCount > index ? "visible" : "hidden"}
          variants={cardAnimationVariants}
        >
          <Card elevation={0} sx={styles.CardContainer}>
            <Box sx={styles.IconContainer}>
              <CardMedia
                component="img"
                image={item.icon}
                alt={`${item.title} icon`}
                sx={styles.CardMedia}
              />
            </Box>
            <CardContent sx={styles.CardContent}>
              <Typography variant="h6" sx={styles.ProductLabelTxt}>
                {item.title}
              </Typography>
              {item.description && (
                <Typography variant="body2" sx={styles.ProductDescriptionTxt}>
                  {item.description}
                </Typography>
              )}
              {["pointsOne", "pointsTwo", "pointsThree", "pointsFour"].map(
                (pointKey) => {
                  const point = item[pointKey as keyof Product];
                  const navigatorKey = `navigator${pointKey
                    .charAt(0)
                    .toUpperCase()}${pointKey.slice(1)}` as keyof Product;
                  if (!point) return null;
                  return (
                    <Typography
                      key={pointKey}
                      variant="body2"
                      sx={styles.OthersLabelTxt}
                      onClick={() =>
                        item[navigatorKey] &&
                        onNavigate(item[navigatorKey] as string)
                      }
                    >
                      • {point}
                    </Typography>
                  );
                }
              )}
              {item.title !== "Others" && item.navigator && (
                <Link
                  component={RouterLink}
                  to={item.navigator.toLowerCase().replace(/\s+/g, "-")}
                  underline="none"
                  sx={styles.LearnMoreButtonTxt}
                >
                  Read More →
                </Link>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </Grid>
    ))}
  </Grid>
);

// Main Component
const OurProducts: React.FC = () => {
  const navigate = useNavigate();
  const { ref, inView } = useInView({
    triggerOnce: false,
    threshold: 0.25,
  });
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (inView) {
      const showNext = () => {
        setRevealedCount((curr) => {
          if (curr >= OUR_PRODUCTS_LIST.length) return curr;
          timeout = setTimeout(showNext, 180);
          return curr + 1;
        });
      };
      setRevealedCount(0);
      timeout = setTimeout(showNext, 200);
    } else {
      setRevealedCount(0);
    }
    return () => clearTimeout(timeout);
    // eslint-disable-next-line
  }, [inView]);

  return (
    <Box
      display="flex"
      alignContent="center"
      justifyContent="center"
      alignItems="center"
      sx={{ paddingTop: 4 }}
    >
      <div className="max-w-screen-xl">
        <Grid alignContent="center" textAlign="center">
          <Box>
            <Typography variant="h4">Our Products</Typography>
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontSize: {
                  sx: ".98rem",
                  md: "1.1rem",
                  lg: "1.2rem",
                },
              }}
            >
              Choose the perfect plan to meet your goals and elevate your
              lifestyle.
            </Typography>
          </Box>
        </Grid>
        <Box sx={styles.OurProductMainContainer} ref={ref}>
          <ProductCardList
            products={OUR_PRODUCTS_LIST}
            revealedCount={revealedCount}
            onNavigate={navigate}
          />
        </Box>
      </div>
    </Box>
  );
};

export default OurProducts;
